import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle, ArrowRight } from "lucide-react";
import { scrollToHash } from "@/components/Navbar";
import { whatsappHref } from "@/lib/contact";

const WHATSAPP_SUNIL = whatsappHref(
  "Hi Ashish, I'm contacting the Homewiz.in independent advisory desk about Fairfox EON investment opportunities. Please share details."
);

const STATS = [
  { value: "5.9 L+", label: "Sq.Ft Grade-A Development" },
  { value: "88", label: "High-Speed Lifts" },
  { value: "3,000+", label: "Car Parking Bays" },
];

const EASE = [0.16, 1, 0.3, 1];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const go = (hash) => (e) => {
    e.preventDefault();
    scrollToHash(hash);
  };

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden">
      <motion.div style={{ y: imgY }} className="absolute inset-0">
        <video
          data-testid="hero-video"
          autoPlay
          muted
          loop
          playsInline
          poster="/eon-cover.jpg"
          aria-label="Walkthrough video of the Fairfox EON towers"
          className="w-full h-[120%] object-cover scale-105"
        >
          <source src="/eon-walkthrough.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C0E] via-[#0B0C0E]/55 to-[#0B0C0E]/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0C0E]/80 via-[#0B0C0E]/30 to-transparent" />
        <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[#0B0C0E]/85 to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full pb-20 pt-40">
        <motion.p
          data-testid="hero-eyebrow"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.35, ease: EASE }}
          className="font-mono text-xs uppercase tracking-[0.35em] text-[#D4AF37] mb-6"
        >
          Fairfox EON · Sector 140A | Noida Expressway
        </motion.p>

        <h1 className="font-display font-semibold text-4xl sm:text-5xl lg:text-[4.75rem] leading-[1.04] tracking-tight text-[#F3F4F6]">
          {["Invest in Noida's Next", "Commercial Destination."].map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                data-testid={`hero-line-${i}`}
                className="block"
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.15, delay: 0.5 + i * 0.14, ease: EASE }}
              >
                {i === 1 ? <span className="gold-text italic">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          data-testid="hero-offer-row"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.95, ease: EASE }}
          className="mt-7 flex flex-wrap items-center gap-3"
        >
          <span
            data-testid="hero-price-chip"
            className="px-5 py-2.5 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/40 font-mono text-xs sm:text-sm tracking-[0.08em] text-[#F3E5AB]"
          >
            Selected opportunities from ₹40 Lacs*
          </span>
          <span
            data-testid="hero-status-chip"
            className="px-5 py-2.5 rounded-full border border-white/15 bg-white/[0.04] backdrop-blur-md font-mono text-xs sm:text-sm uppercase tracking-[0.15em] text-[#9CA3AF]"
          >
            Ready to Move <span className="text-[#D4AF37] mx-1">|</span> Primary &amp; Resale
          </span>
          <p data-testid="hero-independent-disclosure" className="w-full font-mono text-[10px] uppercase tracking-[0.16em] text-[#9CA3AF]">
            Independent Marketing &amp; Investment Advisory · Not the Official Project Website
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.1, ease: EASE }}
          className="mt-8 flex flex-wrap items-center gap-3"
        >
          <a
            href="#opportunities"
            data-testid="hero-explore-units-btn"
            onClick={go("#opportunities")}
            className="group px-7 py-3.5 bg-[#D4AF37] text-[#0B0C0E] font-sans text-xs font-semibold uppercase tracking-[0.18em] rounded-full hover:bg-[#F3E5AB] hover:shadow-[0_0_32px_rgba(212,175,55,0.45)] transition-all duration-300 flex items-center gap-2"
          >
            Explore Opportunities
            <ArrowDown size={14} className="group-hover:translate-y-0.5 transition-transform duration-300" />
          </a>
          <a
            href={WHATSAPP_SUNIL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-btn"
            className="px-7 py-3.5 border border-[#D4AF37]/40 text-[#F3F4F6] font-sans text-xs font-semibold uppercase tracking-[0.18em] rounded-full hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 transition-all duration-300 flex items-center gap-2"
          >
            <MessageCircle size={14} className="text-[#D4AF37]" />
            WhatsApp Ashish
          </a>
        </motion.div>

        <motion.div
          data-testid="hero-journey-strip"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.25, ease: EASE }}
          className="mt-10"
        >
          <a
            href="#journey"
            onClick={go("#journey")}
            className="group inline-flex items-center gap-4 font-mono text-xs sm:text-sm uppercase tracking-[0.3em] text-[#9CA3AF] hover:text-[#F3F4F6] transition-colors duration-300"
          >
            <span>Buy</span>
            <ArrowRight size={14} className="text-[#D4AF37] group-hover:translate-x-1 transition-transform duration-300" />
            <span>Fit Out</span>
            <ArrowRight size={14} className="text-[#D4AF37] group-hover:translate-x-1 transition-transform duration-300" />
            <span className="gold-text">Lease</span>
          </a>
        </motion.div>

        <motion.div
          data-testid="hero-stats"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 1.45 }}
          className="mt-12 grid grid-cols-3 max-w-lg gap-6 border-t border-white/10 pt-6"
        >
          {STATS.map((s) => (
            <div key={s.label}>
              <div className="font-display text-2xl sm:text-3xl font-semibold gold-text">{s.value}</div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[#9CA3AF]">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
